import { MakeLogicType, actions, afterMount, connect, kea, key, listeners, path, props, reducers, selectors } from 'kea'
import { loaders } from 'kea-loaders'
import { subscriptions } from 'kea-subscriptions'

import api from 'lib/api'
import { RichContentEditorType } from 'lib/components/RichContentEditor/types'
import { deleteWithUndo } from 'lib/utils/deleteWithUndo'
import { isEmptyObject } from 'lib/utils/guards'
import { membersLogic } from 'scenes/organization/membersLogic'
import { teamLogic } from 'scenes/teamLogic'
import { userLogic } from 'scenes/userLogic'

import { sidePanelDiscussionLogic } from '~/layout/navigation-3000/sidepanel/panels/discussion/sidePanelDiscussionLogic'
import { CommentType } from '~/types'

import type { UserType } from '../../types'
import type { OrganizationMemberType } from '../../types'
import { discussionsSlug, getTextContent } from './utils'

export type CommentsLogicProps = {
    scope: CommentType['scope']
    item_id?: CommentType['item_id']
    item_context?: CommentType['item_context']
    disabled?: boolean
}

export type CommentWithRepliesType = {
    id: CommentType['id']
    comment?: CommentType // It may have been deleted
    replies: CommentType[]
}

export type CommentContext = {
    context: Record<string, any> | null
    callback?: (event: { sent: boolean }) => void
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface commentsLogicValues {
    meFirstMembers: OrganizationMemberType[] // membersLogic
    currentProjectId: number | string // teamLogic
    user: UserType | null // userLogic
    commentContexts: Record<string, string>
    comments: CommentType[] | null
    commentsLoading: boolean
    commentsWithReplies: CommentWithRepliesType[]
    disabledReasonFor: (comment: CommentType) => string | null
    editingComment: CommentType | null
    editingCommentExistingMentions: number[] | null
    editingCommentRichContentEditor: RichContentEditorType | null
    emojiReactionsByComment: Record<string, Record<string, CommentType[]>>
    isEditingCommentEmpty: boolean
    isEmpty: boolean
    isMyComment: (comment: CommentType) => boolean
    itemContext: CommentContext | null
    key: string
    replyingCommentId: string | null
    richContentEditor: RichContentEditorType | null
    selectedCommentId: string | null
    sortedComments: CommentType[]
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface commentsLogicActions {
    ensureAllMembersLoaded: () => {
        value: true
    } // membersLogic
    incrementCommentCount: () => any // sidePanelDiscussionLogic
    scrollToLastComment: () => {
        value: true
    } // sidePanelDiscussionLogic
    clearItemContext: () => {
        value: true
    }
    completeComment: (comment: CommentType) => {
        comment: CommentType
    }
    completeCommentFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    completeCommentSuccess: (
        comments: CommentType[],
        payload?: {
            comment: CommentType
        }
    ) => {
        comments: CommentType[]
        payload?: {
            comment: CommentType
        }
    }
    deleteComment: (comment: CommentType) => {
        comment: CommentType
    }
    deleteCommentFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    deleteCommentSuccess: (
        comments: CommentType[] | null,
        payload?: {
            comment: CommentType
        }
    ) => {
        comments: CommentType[] | null
        payload?: {
            comment: CommentType
        }
    }
    focusComposer: () => {
        value: true
    }
    loadComments: () => {
        value: true
    }
    loadCommentsFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    loadCommentsSuccess: (
        comments: CommentType[],
        payload?: {
            value: true
        }
    ) => {
        comments: CommentType[]
        payload?: {
            value: true
        }
    }
    maybeLoadComments: () => {
        value: true
    }
    onEditingCommentRichContentEditorUpdate: (isEmpty: boolean) => {
        isEmpty: boolean
    }
    onRichContentEditorUpdate: (isEmpty: boolean) => {
        isEmpty: boolean
    }
    persistEditedComment: () => {
        value: true
    }
    persistEditedCommentFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    persistEditedCommentSuccess: (
        comments: CommentType[],
        payload?: {
            value: true
        }
    ) => {
        comments: CommentType[]
        payload?: {
            value: true
        }
    }
    reopenComment: (comment: CommentType) => {
        comment: CommentType
    }
    reopenCommentFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    reopenCommentSuccess: (
        comments: CommentType[],
        payload?: {
            comment: CommentType
        }
    ) => {
        comments: CommentType[]
        payload?: {
            comment: CommentType
        }
    }
    sendComposedContent: (asTask?: boolean) => {
        asTask: boolean
    }
    sendComposedContentFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    sendComposedContentSuccess: (
        comments: CommentType[],
        payload?: {
            asTask: boolean
        }
    ) => {
        comments: CommentType[]
        payload?: {
            asTask: boolean
        }
    }
    sendEmojiReaction: (
        emoji: string,
        sourceCommentId: string
    ) => {
        emoji: string
        sourceCommentId: string
    }
    sendEmojiReactionFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    sendEmojiReactionSuccess: (
        comments: CommentType[],
        payload?: {
            emoji: string
            sourceCommentId: string
        }
    ) => {
        comments: CommentType[]
        payload?: {
            emoji: string
            sourceCommentId: string
        }
    }
    setCommentContexts: (contexts: Record<string, string>) => {
        contexts: Record<string, string>
    }
    setEditingComment: (comment: CommentType | null) => {
        comment: CommentType | null
    }
    setEditingCommentRichContentEditor: (editor: RichContentEditorType | null) => {
        editor: RichContentEditorType | null
    }
    setItemContext: (
        context: Record<string, any> | null,
        callback?: (event: { sent: boolean }) => void
    ) => {
        callback: ((event: { sent: boolean }) => void) | undefined
        context: Record<string, any> | null
    }
    setReplyingComment: (commentId: string | null) => {
        commentId: string | null
    }
    setRichContentEditor: (editor: RichContentEditorType) => {
        editor: RichContentEditorType
    }
    setSelectedComment: (commentId: string | null) => {
        commentId: string | null
    }
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface commentsLogicMeta {
    key: string
    __keaTypeGenInternalSelectorTypes: {
        key: (arg: any) => string
        sortedComments: (comments: CommentType[] | null) => CommentType[]
        commentsWithReplies: (sortedComments: CommentType[]) => CommentWithRepliesType[]
        emojiReactionsByComment: (sortedComments: CommentType[]) => Record<string, Record<string, CommentType[]>>
        isMyComment: (user: UserType | null) => (comment: CommentType) => boolean
        disabledReasonFor: (user: UserType | null) => (comment: CommentType) => string | null
    }
}

export type commentsLogicType = MakeLogicType<
    commentsLogicValues,
    commentsLogicActions,
    CommentsLogicProps,
    commentsLogicMeta
>

export const commentsLogic = kea<commentsLogicType>([
    path(() => ['scenes', 'notebooks', 'Notebook', 'commentsLogic']),
    props({} as CommentsLogicProps),
    key((props) => `${props.scope}-${props.item_id || ''}`),

    connect(() => ({
        actions: [
            sidePanelDiscussionLogic,
            ['incrementCommentCount', 'scrollToLastComment'],
            membersLogic,
            ['ensureAllMembersLoaded'],
        ],
        values: [userLogic, ['user'], membersLogic, ['meFirstMembers'], teamLogic, ['currentProjectId']],
    })),

    actions({
        loadComments: true,
        focusComposer: true,
        clearItemContext: true,
        maybeLoadComments: true,
        sendComposedContent: (asTask: boolean = false) => ({ asTask }),
        persistEditedComment: true,
        onRichContentEditorUpdate: (isEmpty: boolean) => ({ isEmpty }),
        onEditingCommentRichContentEditorUpdate: (isEmpty: boolean) => ({ isEmpty }),
        sendEmojiReaction: (emoji: string, sourceCommentId: string) => ({ emoji, sourceCommentId }),
        deleteComment: (comment: CommentType) => ({ comment }),
        completeComment: (comment: CommentType) => ({ comment }),
        reopenComment: (comment: CommentType) => ({ comment }),
        setEditingComment: (comment: CommentType | null) => ({ comment }),
        setReplyingComment: (commentId: string | null) => ({ commentId }),
        setSelectedComment: (commentId: string | null) => ({ commentId }),
        setCommentContexts: (contexts: Record<string, string>) => ({ contexts }),
        setItemContext: (context: Record<string, any> | null, callback?: (event: { sent: boolean }) => void) => ({
            context,
            callback,
        }),
        setRichContentEditor: (editor: RichContentEditorType) => ({ editor }),
        setEditingCommentRichContentEditor: (editor: RichContentEditorType | null) => ({ editor }),
    }),
    reducers({
        replyingCommentId: [
            null as string | null,
            {
                setReplyingComment: (_, { commentId }) => commentId,
                sendComposedContentSuccess: () => null,
            },
        ],
        selectedCommentId: [
            null as string | null,
            {
                setSelectedComment: (_, { commentId }) => commentId,
                setReplyingComment: (state, { commentId }) => commentId ?? state,
                sendComposedContentSuccess: () => null,
            },
        ],
        commentContexts: [
            {} as Record<string, string>,
            {
                setCommentContexts: (_, { contexts }) => contexts,
            },
        ],
        itemContext: [
            null as CommentContext | null,
            {
                setItemContext: (_, itemContext) => (itemContext.context ? itemContext : null),
                sendComposedContentSuccess: () => null,
            },
        ],
        richContentEditor: [
            null as RichContentEditorType | null,
            {
                setRichContentEditor: (_, { editor }) => editor,
            },
        ],
        isEmpty: [
            true as boolean,
            {
                onRichContentEditorUpdate: (_, { isEmpty }) => isEmpty,
            },
        ],
        editingComment: [
            null as CommentType | null,
            {
                setEditingComment: (_, { comment }) => comment,
                persistEditedCommentSuccess: () => null,
            },
        ],
        editingCommentRichContentEditor: [
            null as RichContentEditorType | null,
            {
                setEditingCommentRichContentEditor: (_, { editor }) => editor,
                persistEditedCommentSuccess: () => null,
            },
        ],
        editingCommentExistingMentions: [
            null as number[] | null,
            {
                setEditingCommentRichContentEditor: (_, { editor }) => editor?.getMentions() ?? [],
                persistEditedCommentSuccess: () => null,
            },
        ],
        isEditingCommentEmpty: [
            false as boolean,
            {
                onEditingCommentRichContentEditorUpdate: (_, { isEmpty }) => isEmpty,
                persistEditedCommentSuccess: () => false,
            },
        ],
    }),
    loaders(({ props, values, actions }) => ({
        comments: [
            null as CommentType[] | null,
            {
                loadComments: async () => {
                    if (!props.item_id) {
                        // A per-item discussion must target a specific item. Listing with only a
                        // scope makes the backend return every comment in that scope across the
                        // team, so fail closed rather than leak other items' comments.
                        return []
                    }

                    const response = await api.comments.list({
                        scope: props.scope,
                        item_id: props.item_id,
                    })

                    return response.results
                },
                sendComposedContent: async ({ asTask }) => {
                    const existingComments = values.comments ?? []

                    if (!props.item_id) {
                        console.error('Failed to create a comment because no item_id was provided')
                        return existingComments
                    }

                    if (values.richContentEditor?.isEmpty()) {
                        console.error('Failed to create a comment because the content was empty')
                        return existingComments
                    }

                    let itemContext: Record<string, any> | undefined = {
                        ...values.itemContext?.context,
                        ...props.item_context,
                    }
                    if (isEmptyObject(itemContext)) {
                        itemContext = undefined
                    }

                    const mentions = values.richContentEditor?.getMentions() ?? []

                    const content = values.richContentEditor?.getJSON()

                    const textContent = getTextContent(content, values.meFirstMembers)

                    const composerAnchor = values.itemContext?.context
                    const isNewAnchoredThread =
                        composerAnchor && (composerAnchor.type === 'mark' || composerAnchor.type === 'node')

                    const isReply = !isNewAnchoredThread && !!values.replyingCommentId

                    const newComment = await api.comments.create({
                        rich_content: content,
                        content: textContent,
                        scope: props.scope,
                        item_id: props.item_id,
                        item_context: itemContext,
                        source_comment: isNewAnchoredThread ? undefined : (values.replyingCommentId ?? undefined),
                        mentions,
                        slug: discussionsSlug(props.scope, props.item_id),
                        is_task: asTask && !isReply,
                    })

                    values.itemContext?.callback?.({ sent: true })
                    return [...existingComments, newComment]
                },

                persistEditedComment: async () => {
                    const existingComments = values.comments ?? []
                    const editedComment = values.editingComment

                    if (!editedComment) {
                        return existingComments
                    }

                    const originalComment = existingComments.find((c) => c.id === editedComment.id)

                    if (!originalComment) {
                        return existingComments
                    }

                    const previousMentions = values.editingCommentExistingMentions ?? []
                    const currentMentions = values.editingCommentRichContentEditor?.getMentions() ?? []
                    const newMentions = currentMentions.filter((m) => !previousMentions.includes(m))

                    const { id, rich_content } = editedComment

                    const textContent = getTextContent(rich_content, values.meFirstMembers)

                    const updatedComment = await api.comments.update(id, {
                        rich_content,
                        content: textContent,
                        mentions: newMentions,
                        slug: discussionsSlug(props.scope, props.item_id),
                    })
                    return [...existingComments.filter((c) => c.id !== editedComment.id), updatedComment]
                },

                deleteComment: async ({ comment }) => {
                    await deleteWithUndo({
                        endpoint: `projects/${values.currentProjectId}/comments`,
                        object: { name: comment.item_context?.is_emoji ? 'Reaction' : 'Comment', id: comment.id },
                        callback: (isUndo) => {
                            if (isUndo) {
                                actions.loadCommentsSuccess([
                                    ...(values.comments?.filter((c) => c.id !== comment.id) ?? []),
                                    comment,
                                ])
                            }
                        },
                    })

                    return values.comments?.filter((c) => c.id !== comment.id) ?? null
                },

                sendEmojiReaction: async ({ emoji, sourceCommentId }) => {
                    const existingComments = values.comments ?? []

                    const newComment = await api.comments.create({
                        content: emoji,
                        scope: props.scope,
                        item_id: props.item_id,
                        source_comment: sourceCommentId,
                        item_context: {
                            is_emoji: true,
                        },
                        mentions: [],
                    })

                    return [...existingComments, newComment]
                },

                completeComment: async ({ comment }) => {
                    const updated = await api.comments.complete(comment.id)
                    const existing = values.comments ?? []
                    return existing.map((c) => (c.id === updated.id ? updated : c))
                },

                reopenComment: async ({ comment }) => {
                    const updated = await api.comments.reopen(comment.id)
                    const existing = values.comments ?? []
                    return existing.map((c) => (c.id === updated.id ? updated : c))
                },
            },
        ],
    })),

    listeners(({ values, actions }) => ({
        setReplyingComment: ({ commentId }) => {
            if (commentId) {
                actions.clearItemContext()
            }
        },
        clearItemContext: () => {
            values.itemContext?.callback?.({ sent: false })
            actions.setItemContext(null)
        },
        setItemContext: ({ context }) => {
            if (context) {
                values.richContentEditor?.focus()
            }
        },
        focusComposer: () => {
            values.richContentEditor?.focus()
        },
        maybeLoadComments: () => {
            if (!values.comments && !values.commentsLoading) {
                actions.loadComments()
            }
        },
        sendComposedContentSuccess: () => {
            actions.scrollToLastComment()
            actions.incrementCommentCount()
            values.richContentEditor?.clear()
        },
        loadCommentsSuccess: () => {
            actions.scrollToLastComment()
        },
    })),

    selectors({
        key: [() => [(_, props) => props], (props): string => `${props.scope}-${props.item_id || ''}`],
        sortedComments: [
            (s) => [s.comments],
            (comments: CommentType[] | null) => {
                return comments?.sort((a, b) => (a.created_at > b.created_at ? 1 : -1)) ?? []
            },
        ],

        commentsWithReplies: [
            (s) => [s.sortedComments],
            (sortedComments: CommentType[]) => {
                // NOTE: We build a tree of comments and replies here.
                // Comments may have been deleted so if we have a reply to a comment that no longer exists,
                // we still create the CommentWithRepliesType but with a null comment.

                const commentsById: Record<string, CommentWithRepliesType> = {}

                for (const comment of sortedComments ?? []) {
                    // Skip emoji reactions from the reply tree - they'll be handled separately
                    if (comment.item_context?.is_emoji) {
                        continue
                    }

                    let commentsWithReplies = commentsById[comment.source_comment ?? comment.id]

                    if (!commentsWithReplies) {
                        commentsById[comment.source_comment ?? comment.id] = commentsWithReplies = {
                            id: comment.source_comment ?? comment.id,
                            comment: undefined,
                            replies: [],
                        }
                    }

                    if (commentsWithReplies.id === comment.id) {
                        commentsWithReplies.comment = comment
                    } else {
                        commentsWithReplies.replies.push(comment)
                    }
                }

                return Object.values(commentsById)
            },
        ],

        emojiReactionsByComment: [
            (s) => [s.sortedComments],
            (sortedComments: CommentType[]) => {
                const reactions: Record<CommentType['id'], Record<string, CommentType[]>> = {}

                for (const comment of sortedComments ?? []) {
                    if (comment.item_context?.is_emoji && comment.source_comment) {
                        if (!reactions[comment.source_comment]) {
                            reactions[comment.source_comment] = {}
                        }
                        // TODO: emoji reactions still use the content field for now
                        const emoji = comment.content!
                        if (!reactions[comment.source_comment][emoji]) {
                            reactions[comment.source_comment][emoji] = []
                        }
                        reactions[comment.source_comment][emoji].push(comment)
                    }
                }

                return reactions
            },
        ],

        isMyComment: [
            (s) => [s.user],
            (user: null | import('~/types').UserType) => {
                return (comment: CommentType): boolean => comment.created_by?.uuid === user?.uuid
            },
        ],

        disabledReasonFor: [
            (s) => [s.user],
            (user: null | import('~/types').UserType) => {
                return (comment: CommentType): string | null =>
                    comment.created_by?.uuid === user?.uuid ? null : 'You can only delete your own comments'
            },
        ],
    }),

    subscriptions(({ actions }) => ({
        replyingCommentId: (value: string): void => {
            if (value) {
                actions.focusComposer()
            }
        },
    })),

    afterMount(({ actions }) => {
        actions.ensureAllMembersLoaded()
    }),
])
